import React, { useCallback, useContext, useMemo } from 'react';
import { Form, Formik } from 'formik';
import { typedFormikField } from 'components/fields/utils/typedFormikField';
import { FormikTextField, FormikCheckboxField } from 'components/fields/FormikTextField';
import {
    firstNameValidator,
    fundraisingGroupValidator,
    lastNameValidator,
    studentNameValidator,
    studentNameValidatorRequired,
} from 'common/modules/form-validation/validators';
import { FamilyEnrollmentContext } from 'features/family-enrollment/context';
import { BaseStep } from '../BaseStep';
import { ButtonControls } from '../utils';
import { OrganizationType } from 'common/api/store/models/OrganizationType';
import { FieldSpacer } from 'components/fields/FieldSpacer';
import { logParticipantEnrollment } from 'config/analytics';
import { IS_FLIPGIVE } from 'common/env';
import { createStyles } from 'utils/createStyle';
import { useLocation } from 'react-router-dom';
import { Routes } from 'routes';

const marketingOptInStyles = createStyles({
    wrapper: [
        '',
        {
            '& div.flex.flex-row': { alignItems: 'flex-start' },
        },
    ],
});

const MARKETING_OPT_IN_COPY =
    "Yes, I'd like to receive emails from FlipGive so I don't miss out on bonus earnings, online shopping deals, and new brand updates. I understand I can unsubscribe at any time.";
const FLIPGIVE_SUPPORT_PHONE = '800-727-4715';
const FLIPGIVE_SUPPORT_PHONE_OPTION = 'Option 3';
const FLIPGIVE_SUPPORT_EMAIL = 'support@flipgive.com';

interface SocialEarningForFormValues {
    firstName: string;
    lastName: string;
    studentName: string;
    fundraisingGroup: string;
    marketingOptIn: boolean;
}

const SocialEarningForField = typedFormikField<SocialEarningForFormValues>()(FormikTextField);

export const SocialEarningForStep = () => {
    const {
        organization,
        userAccountInfo,
        updateUserAccountInfo,
        goToNextStep,
        isEnrollmentTestVariant,
        isSocialRegistrationEnabled,
    } = useContext(FamilyEnrollmentContext);
    const location = useLocation();
    const isSocialEarningForRoute = location.pathname === Routes.EnrollFamilySocialEarningFor;
    const shouldShowAccountDetailsLayout =
        isSocialRegistrationEnabled ||
        userAccountInfo.registrationMethod === 'social' ||
        isSocialEarningForRoute;

    const isCanadianUser = organization?.countryCode === 'CA' || IS_FLIPGIVE;
    const showMarketingOptIn = isCanadianUser;

    const initialValues: SocialEarningForFormValues = {
        firstName: userAccountInfo?.firstName || '',
        lastName: userAccountInfo?.lastName || '',
        studentName: userAccountInfo?.studentName || '',
        fundraisingGroup: userAccountInfo?.fundraisingGroup || '',
        marketingOptIn: showMarketingOptIn ? userAccountInfo?.marketingOptIn ?? false : false,
    };

    const enrollCode = useMemo(() => localStorage.getItem('RR_enrollmentCode') || '', []);
    const enrollCodeFromP2P =
        enrollCode.startsWith('RR') &&
        (OrganizationType.ParochialSchool === organization?.type ||
            OrganizationType.BandMusic === organization?.type ||
            OrganizationType.AthleticGroup === organization?.type);

    const onSubmit = useCallback(
        async ({
            firstName,
            lastName,
            studentName,
            fundraisingGroup,
            marketingOptIn,
        }: SocialEarningForFormValues) => {
            updateUserAccountInfo((_) => ({
                ..._,
                ...(shouldShowAccountDetailsLayout && { firstName, lastName }),
                studentName,
                fundraisingGroup,
                marketingOptIn: showMarketingOptIn ? marketingOptIn : false,
            }));

            logParticipantEnrollment(isEnrollmentTestVariant ? 4 : 3, 'social_earning_for', {
                experimentName: 'participant_enrollment_test_web',
                experimentVariant: isEnrollmentTestVariant ? 'variant' : 'control',
            });
            goToNextStep();
        },
        [
            updateUserAccountInfo,
            goToNextStep,
            isEnrollmentTestVariant,
            shouldShowAccountDetailsLayout,
            showMarketingOptIn,
        ]
    );

    return (
        <BaseStep
            contentClassName="text-md sm:text-lg font-thin pb-4"
            heading={
                shouldShowAccountDetailsLayout ? 'Account Details' : 'Who are you earning for?'
            }
            subheading={null}
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit}>
                {(formikProps) => (
                    <Form>
                        <div style={shouldShowAccountDetailsLayout ? { marginTop: 0 } : undefined}>
                            {shouldShowAccountDetailsLayout ? (
                                <section className="max-w-[432px]">
                                    <p className="text-brand-dark text-lg leading-7">
                                        We need some basic information to help you start
                                        <br />
                                        earning for {organization?.name || 'your organization'}.
                                    </p>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 mt-6">
                                        <SocialEarningForField
                                            name="firstName"
                                            label="First Name"
                                            validate={firstNameValidator}
                                        />
                                        <SocialEarningForField
                                            name="lastName"
                                            label="Last Name"
                                            validate={lastNameValidator}
                                        />
                                    </div>

                                    <div className="text-brand-dark text-lg leading-7 mt-[38px]">
                                        Who are you earning for?
                                    </div>

                                    <div className="mt-5">
                                        <SocialEarningForField
                                            name="studentName"
                                            label={
                                                <label className="font-bold">
                                                    Student's Name{' '}
                                                    {!enrollCodeFromP2P ? (
                                                        <span className="text-grey-medium font-normal">
                                                            (optional)
                                                        </span>
                                                    ) : null}
                                                </label>
                                            }
                                            validate={
                                                !enrollCodeFromP2P
                                                    ? studentNameValidator
                                                    : studentNameValidatorRequired
                                            }
                                        />
                                        {!formikProps.errors.studentName ||
                                        !formikProps.touched.studentName ? (
                                            <span className="mt-2 text-grey-medium text-xs">
                                                Enter the name of the person you are earning for;
                                                this could be an athlete, musician, etc.
                                            </span>
                                        ) : null}
                                    </div>

                                    <div className="mt-5">
                                        <SocialEarningForField
                                            name="fundraisingGroup"
                                            label={
                                                <label className="font-bold">
                                                    Group{' '}
                                                    <span className="text-grey-medium font-normal">
                                                        (optional)
                                                    </span>
                                                </label>
                                            }
                                            validate={fundraisingGroupValidator}
                                        />
                                        <span className="mt-2 text-grey-medium text-xs">
                                            Team name, classroom, etc.
                                        </span>
                                    </div>

                                    {showMarketingOptIn && (
                                        <>
                                            <div className={marketingOptInStyles.wrapper + ' mt-9'}>
                                                <FormikCheckboxField
                                                    name="marketingOptIn"
                                                    id="marketingOptIn"
                                                    label={MARKETING_OPT_IN_COPY}
                                                    className="text-grey-1 text-sm font-semibold pl-1 leading-4"
                                                    checked={formikProps.values.marketingOptIn}
                                                />
                                            </div>
                                            {IS_FLIPGIVE && (
                                                <div className="text-xs text-grey-2 mt-3">
                                                    <a
                                                        href={`tel:${FLIPGIVE_SUPPORT_PHONE}`}
                                                        className="text-brand underline"
                                                    >
                                                        {FLIPGIVE_SUPPORT_PHONE}
                                                    </a>{' '}
                                                    ({FLIPGIVE_SUPPORT_PHONE_OPTION}) |{' '}
                                                    <a
                                                        href={`mailto:${FLIPGIVE_SUPPORT_EMAIL}`}
                                                        className="text-brand underline"
                                                    >
                                                        {FLIPGIVE_SUPPORT_EMAIL}
                                                    </a>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </section>
                            ) : (
                                <section className="grid grid-cols-1 md:grid-cols-1 gap-x-6 gap-y-6 my-4 pt-2">
                                    <div>
                                        <SocialEarningForField
                                            name="studentName"
                                            label={
                                                <label className="font-bold">
                                                    Student's Name{' '}
                                                    {!enrollCodeFromP2P ? (
                                                        <span className="text-grey-medium font-normal">
                                                            (optional)
                                                        </span>
                                                    ) : null}
                                                </label>
                                            }
                                            validate={
                                                !enrollCodeFromP2P
                                                    ? studentNameValidator
                                                    : studentNameValidatorRequired
                                            }
                                        />
                                        {!formikProps.errors.studentName ||
                                        !formikProps.touched.studentName ? (
                                            <span className="mt-2 text-grey-medium text-xs">
                                                Enter the name of the person you are earning for;
                                                this could be an athlete, musician, etc.
                                            </span>
                                        ) : null}
                                    </div>
                                    <div>
                                        <SocialEarningForField
                                            name="fundraisingGroup"
                                            label={
                                                <label className="font-bold">
                                                    Group{' '}
                                                    <span className="text-grey-medium font-normal">
                                                        (optional)
                                                    </span>
                                                </label>
                                            }
                                            validate={fundraisingGroupValidator}
                                        />
                                        <span className="mt-2 text-grey-medium text-xs">
                                            Team name, classroom, etc.
                                        </span>
                                    </div>
                                    {showMarketingOptIn && (
                                        <>
                                            <FieldSpacer />
                                            <div className={marketingOptInStyles.wrapper}>
                                                <FormikCheckboxField
                                                    name="marketingOptIn"
                                                    id="marketingOptIn"
                                                    label={MARKETING_OPT_IN_COPY}
                                                    className="text-grey-1 text-sm font-semibold pl-1 leading-4"
                                                    checked={formikProps.values.marketingOptIn}
                                                />
                                            </div>
                                            {IS_FLIPGIVE && (
                                                <div className="text-xs text-grey-2">
                                                    <a
                                                        href={`tel:${FLIPGIVE_SUPPORT_PHONE}`}
                                                        className="text-brand underline"
                                                    >
                                                        {FLIPGIVE_SUPPORT_PHONE}
                                                    </a>{' '}
                                                    ({FLIPGIVE_SUPPORT_PHONE_OPTION}) |{' '}
                                                    <a
                                                        href={`mailto:${FLIPGIVE_SUPPORT_EMAIL}`}
                                                        className="text-brand underline"
                                                    >
                                                        {FLIPGIVE_SUPPORT_EMAIL}
                                                    </a>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </section>
                            )}
                        </div>
                        <ButtonControls
                            nextLabel={shouldShowAccountDetailsLayout ? 'Get Started' : 'Next'}
                            containerClassName={
                                shouldShowAccountDetailsLayout
                                    ? 'w-full max-w-[432px] mx-auto'
                                    : undefined
                            }
                            nextButtonClassName={
                                shouldShowAccountDetailsLayout ? 'w-full' : undefined
                            }
                        />
                    </Form>
                )}
            </Formik>
        </BaseStep>
    );
};
